import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getQuestionById, deleteQuestion, parseOptions } from '../../services/adminApi';
import { toast } from 'react-toastify';

const QuestionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchQuestion();
  }, [id]);

  const fetchQuestion = async () => {
    setLoading(true);
    try {
      const data = await getQuestionById(id);
      setQuestion(data);
    } catch (error) {
      toast.error(`Failed to fetch question: ${error.message}`);
      navigate('/admin/questions');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete this question?\n\nID: ${question.id}\n"${question.question_text.substring(0, 100)}..."\n\nThis action cannot be undone.`)) {
      return;
    }
    
    setDeleting(true);
    try {
      await deleteQuestion(id);
      toast.success('Question deleted successfully!');
      navigate('/admin/questions');
    } catch (error) {
      toast.error(`Failed to delete question: ${error.message}`);
      setDeleting(false);
    }
  };

  const formatTopic = (topic) => {
    if (!topic) return 'N/A';
    return topic.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatQuestionType = (type) => {
    if (!type) return 'N/A';
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getDifficultyBadgeClass = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy': return 'badge-success';
      case 'medium': return 'badge-warning';
      case 'hard': return 'badge-danger';
      default: return 'badge-secondary';
    }
  };

  const getTypeBadgeClass = (type) => {
    switch (type) {
      case 'multiple_choice': return 'badge-primary';
      case 'true_false': return 'badge-info';
      case 'fill_in_blank': return 'badge-secondary';
      case 'short_answer': return 'badge-dark';
      default: return 'badge-light';
    }
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading question details...</p>
        </div>
      </div>
    );
  }

  if (!question) {
    return (
      <div className="container-fluid">
        <div className="text-center py-5">
          <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
          <h4>Question Not Found</h4>
          <p className="text-muted">The question you're looking for doesn't exist.</p>
          <Link to="/admin/questions" className="btn btn-primary">
            <i className="fas fa-arrow-left"></i> Back to Questions List
          </Link>
        </div>
      </div>
    );
  }

  const options = parseOptions(question.options);
  
  // Determine correct index - use correct_option_index if available, otherwise find by correct_answer
  let correctIndex = question.correct_option_index;
  if (correctIndex === null || correctIndex === undefined) {
    // Find the index of the correct answer in options array
    correctIndex = options.findIndex(opt => 
      opt && question.correct_answer && 
      opt.toLowerCase().trim() === question.correct_answer.toLowerCase().trim()
    );
  }

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <Link to="/admin/questions" className="btn btn-link text-decoration-none pl-0">
            <i className="fas fa-arrow-left"></i> Back to Questions
          </Link>
          <h1 className="h3 mb-0 text-gray-800">Question #{question.id}</h1>
        </div>
        <div>
          <Link
            to={`/admin/questions/${question.id}/edit`}
            className="btn btn-warning btn-sm shadow-sm mr-2"
          >
            <i className="fas fa-edit fa-sm"></i> Edit Question
          </Link>
          <button
            className="btn btn-danger btn-sm shadow-sm"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <i className="fas fa-spinner fa-spin fa-sm"></i> Deleting...
              </>
            ) : (
              <>
                <i className="fas fa-trash fa-sm"></i> Delete Question
              </>
            )}
          </button>
        </div>
      </div>

      {/* Question Details Card */}
      <div className="card shadow mb-4">
        <div className="card-header py-3">
          <h6 className="m-0 font-weight-bold text-primary">Question Details</h6>
        </div>
        <div className="card-body">
          {/* Metadata Badges */}
          <div className="mb-4">
            <span className={`badge ${getTypeBadgeClass(question.question_type)} mr-2`}>
              {formatQuestionType(question.question_type)}
            </span>
            <span className={`badge ${getDifficultyBadgeClass(question.difficulty)} mr-2`}>
              {question.difficulty || 'N/A'}
            </span>
            <span className="badge badge-secondary">
              {formatTopic(question.topic)}
            </span>
          </div>

          {/* Question Text */}
          <div className="mb-4">
            <h5 className="text-primary mb-2">Question:</h5>
            <div className="p-3 bg-light rounded border">
              <p className="mb-0" style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
                {question.question_text}
              </p>
            </div>
          </div>

          {/* Options (for multiple choice and true/false) */}
          {(question.question_type === 'multiple_choice' || question.question_type === 'true_false') && options.length > 0 && (
            <div className="mb-4">
              <h5 className="text-primary mb-3">Options:</h5>
              <div className="pl-3">
                {options.map((option, index) => {
                  const isCorrect = index === correctIndex;
                  return (
                    <div
                      key={index}
                      className={`p-3 mb-2 rounded border ${
                        isCorrect
                          ? 'bg-success text-white border-success'
                          : 'bg-white border-secondary'
                      }`}
                      style={{
                        boxShadow: isCorrect ? '0 0 10px rgba(28, 200, 138, 0.3)' : 'none'
                      }}
                    >
                      <div className="d-flex align-items-start">
                        <div className="mr-3">
                          {isCorrect ? (
                            <i className="fas fa-check-circle fa-lg"></i>
                          ) : (
                            <i className="far fa-circle fa-lg text-muted"></i>
                          )}
                        </div>
                        <div className="flex-grow-1">
                          <strong className="mr-2">Option {index + 1}:</strong>
                          {option}
                          {isCorrect && (
                            <span className="badge badge-light ml-2">
                              <i className="fas fa-star"></i> Correct Answer
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Correct Answer (for non-multiple choice and non-true/false) */}
          {question.question_type !== 'multiple_choice' && question.question_type !== 'true_false' && (
            <div className="mb-4">
              <h5 className="text-primary mb-2">Correct Answer:</h5>
              <div className="p-3 bg-success text-white rounded border border-success">
                <i className="fas fa-check-circle mr-2"></i>
                {question.correct_answer}
              </div>
            </div>
          )}

          {/* Explanation */}
          {question.explanation && (
            <div className="mb-4">
              <h5 className="text-primary mb-2">Explanation:</h5>
              <div className="p-3 bg-light rounded border">
                <p className="mb-0" style={{ lineHeight: '1.6' }}>
                  {question.explanation}
                </p>
              </div>
            </div>
          )}

          {/* Additional Information */}
          <div className="row mt-4 pt-4 border-top">
            <div className="col-md-6">
              <h6 className="text-muted small mb-2">NORMALIZED ANSWER</h6>
              <p className="mb-3">
                <code className="bg-light p-2 rounded">{question.normalized_correct_answer || 'N/A'}</code>
              </p>
            </div>
            {question.correct_option_index !== null && (
              <div className="col-md-6">
                <h6 className="text-muted small mb-2">CORRECT OPTION INDEX</h6>
                <p className="mb-3">
                  <code className="bg-light p-2 rounded">{question.correct_option_index}</code>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Metadata Card */}
      <div className="card shadow mb-4">
        <div className="card-header py-3">
          <h6 className="m-0 font-weight-bold text-primary">Question Metadata</h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-3">
              <p className="mb-2">
                <strong>Question ID:</strong>
                <br />
                <span className="text-muted">{question.id}</span>
              </p>
            </div>
            <div className="col-md-3">
              <p className="mb-2">
                <strong>Question Type:</strong>
                <br />
                <span className="text-muted">{formatQuestionType(question.question_type)}</span>
              </p>
            </div>
            <div className="col-md-3">
              <p className="mb-2">
                <strong>Difficulty Level:</strong>
                <br />
                <span className="text-muted">{question.difficulty || 'N/A'}</span>
              </p>
            </div>
            <div className="col-md-3">
              <p className="mb-2">
                <strong>Topic:</strong>
                <br />
                <span className="text-muted">{formatTopic(question.topic)}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="d-flex justify-content-between mb-4">
        <Link to="/admin/questions" className="btn btn-secondary">
          <i className="fas fa-arrow-left"></i> Back to List
        </Link>
        <div>
          <Link
            to={`/admin/questions/${question.id}/edit`}
            className="btn btn-warning mr-2"
          >
            <i className="fas fa-edit"></i> Edit Question
          </Link>
          <button
            className="btn btn-danger"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Deleting...
              </>
            ) : (
              <>
                <i className="fas fa-trash"></i> Delete Question
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionDetail;
